/**
 * Free gift cart helpers for Concept:
 * - Sync gift qty with main product
 * - Remove gifts when main is removed
 * - Prune orphan gift lines
 * - Block direct qty/remove on gift lines
 */
(function () {
  const routes = (window.theme && theme.routes) || {};
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const cartUrl = routes.cart_url || `${root}cart.js`;
  const changeUrl = routes.cart_change_url || `${root}cart/change.js`;
  const updateUrl = routes.cart_update_url || `${root}cart/update.js`;

  function lineItemIsGift(item) {
    if (!item || !item.properties) return false;
    const g = item.properties._gift;
    return g === true || g === 'true' || String(g).toLowerCase() === 'true';
  }

  function cartLineVariantId(item) {
    if (!item) return null;
    const v = item.variant_id != null ? item.variant_id : item.id;
    return v != null ? String(v) : null;
  }

  function getGiftFor(props) {
    if (!props) return null;
    return props._gift_for || props.gift_for || props._gift_main_product || null;
  }

  async function fetchCart() {
    const res = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
    return res.json();
  }

  async function pruneOrphanGiftLines(cart) {
    const items = cart?.items || [];
    const mainVariantIds = new Set(
      items.filter((item) => !lineItemIsGift(item)).map((item) => cartLineVariantId(item)).filter(Boolean)
    );
    const orphanKeys = [];
    for (const item of items) {
      if (!lineItemIsGift(item)) continue;
      const gf = getGiftFor(item.properties);
      if (gf == null || !mainVariantIds.has(String(gf))) {
        orphanKeys.push(item.key);
      }
    }
    if (orphanKeys.length === 0) return cart;

    const updates = {};
    orphanKeys.forEach((key) => {
      updates[key] = 0;
    });
    await fetch(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    return fetchCart();
  }

  async function syncGiftsForMain(cart, mainKey, targetQuantity) {
    const mainItem = cart.items?.find((item) => item.key === mainKey);
    if (!mainItem || lineItemIsGift(mainItem)) {
      return pruneOrphanGiftLines(cart);
    }

    const mainVariantId = cartLineVariantId(mainItem);
    const related = cart.items.filter(
      (item) => lineItemIsGift(item) && String(getGiftFor(item.properties) ?? '').trim() === mainVariantId
    );
    if (related.length === 0) return cart;

    const qty = Number.isFinite(targetQuantity) ? Math.max(0, targetQuantity) : 0;
    const updates = {};
    related.forEach((gift) => {
      updates[gift.key] = qty;
    });
    await fetch(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    return fetchCart();
  }

  async function attachSections(cart, sectionIds) {
    if (!sectionIds || !sectionIds.length) return cart;
    cart.sections = cart.sections || {};
    await Promise.all(
      sectionIds.map(async (sid) => {
        try {
          const html = await (await fetch(`${root}?section_id=${encodeURIComponent(sid)}`)).text();
          cart.sections[sid] = html;
        } catch (e) {
          console.warn('free-gift section refresh', e);
        }
      })
    );
    return cart;
  }

  function findLineElement(lineKey) {
    return (
      document.getElementById(`CartDrawer-Item-${lineKey}`) ||
      document.getElementById(`CartItem-${lineKey}`)
    );
  }

  function patchCartItems() {
    const CartItems = customElements.get('cart-items');
    if (!CartItems || CartItems.prototype.__freeGiftPatched) return;
    CartItems.prototype.__freeGiftPatched = true;

    CartItems.prototype.updateQuantity = function (line, quantity, name, target) {
      const lineEl = findLineElement(line);
      if (lineEl && lineEl.hasAttribute('data-gift-line')) {
        this.disableLoading?.(line);
        return;
      }

      this.enableLoading(line);

      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      fetch(changeUrl, {
        ...theme.utils.fetchConfig(),
        body: JSON.stringify({ id: line, quantity, sections: sectionsToBundle }),
      })
        .then((response) => response.json())
        .then(async (parsedState) => {
          if (parsedState.status || parsedState.errors) {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-items',
              cart: parsedState,
              target,
              line,
              name,
            });
            return;
          }

          let cart = await fetchCart();
          cart = await syncGiftsForMain(cart, line, quantity);
          cart = await pruneOrphanGiftLines(cart);
          cart = await attachSections(cart, sectionsToBundle);

          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items',
            cart,
            target,
            line,
            name,
          });
        })
        .catch((error) => {
          if (error.name !== 'AbortError') console.error(error);
          this.disableLoading?.(line);
        });
    };
  }

  if (customElements.get('cart-items')) {
    patchCartItems();
  } else {
    customElements.whenDefined('cart-items').then(patchCartItems);
  }
})();
